import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RotateCw, Volume2, VolumeX, CheckCheck } from 'lucide-react';
import { actionsFor } from '@paradigm/notifications';
import { openingLane } from '@paradigm/notifications/InboxPlate';
import { useNotifications } from '../../contexts/NotificationContext';
import { useGroup } from '../../contexts/GroupContext';
import api from '../../services/api';
import { useToast } from '../../components/common';
import NucleusPage, { FindField, Verb } from '../../components/nucleus/NucleusPage';
import Segmented from '../../components/nucleus/Segmented';
import Inspector, { Band, Facts, Note, useOpen } from '../../components/nucleus/Inspector';
import DataTable from '../../components/nucleus/DataTable';
import { TitledSet } from '../../components/nucleus/SetHead';
import { usePlace } from '../../nucleus/place';
import { LANES, LANE_EMPTY, LANE_WORD, answerLabel } from './activityData';
import { destinationOf, canDismiss } from '../../nucleus/notifySpace';
import { ago, whenOf } from '../../nucleus/format';

// THE INBOX — what arrived for you in this space, in lanes: To do (answer it),
// Feed (what happened), Security (sign-ins and grants), or Everything. A row
// pressed is read, and opens in the inspector with every answer it takes:
// Go to it, the queue's answers (Join…, Accept…, Review…, Decline), Dismiss.

const when = (iso) => (iso ? new Date(iso).getTime() : null);

export default function InboxView() {
    const { section, page, groupId, spaceName, inSpace, navigate } = usePlace();
    const { switchToPersonal } = useGroup();
    const toast = useToast();
    const {
        notifications, unreadCount, loading, hasMore, loadingMore, loadMore,
        fetchNotifications, patchRead, patchDismissed, patchAllRead, patchAnswered, lane: ctxLane, setLane, counts,
        soundEnabled, toggleSound, permission, askPermission,
    } = useNotifications();
    const [params, setParams] = useSearchParams();
    const [openId, open, close] = useOpen();
    const [find, setFind] = useState('');
    // asked → loading → done: rows (or "nothing here") show once the lane asked for has been read
    const [phase, setPhase] = useState('asked');
    const space = groupId || 'personal';

    // THE LANE IS IN THE ADDRESS (?lane=). With none named, the first lane with
    // something unread opens, else the feed.
    const named = LANES.some((l) => l.id === params.get('lane')) ? params.get('lane') : null;
    const lane = named || openingLane(counts?.lanes);
    // …and once chosen it is written down, so answering the last unread row
    // of a lane never moves you to another under your feet.
    const countsRead = counts?.space !== undefined; // the context's placeholder has no space; the server's answer does
    useEffect(() => {
        if (named || !countsRead) return;
        setParams((p) => { const n = new URLSearchParams(p); n.set('lane', lane); return n; }, { replace: true });
    }, [named, countsRead, lane, setParams]);
    const pickLane = (id) => setParams((p) => { const n = new URLSearchParams(p); n.set('lane', id); n.delete('open'); return n; });

    // The context fetches just the lane shown (so "Older" pages through it);
    // leaving hands it back to everything.
    const wanted = lane === 'all' ? null : lane;
    useEffect(() => {
        const already = ctxLane === wanted;
        setPhase(already ? 'done' : 'asked');
        setLane(wanted);
        // a lane the context was already on is not fetched again; one asked for
        // settles when its read finishes (or, should none start, shortly)
        const t = already ? null : setTimeout(() => setPhase((p) => (p === 'asked' ? 'done' : p)), 2000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wanted, setLane]);
    useEffect(() => () => setLane(null), [setLane]);
    useEffect(() => {
        if (loading) setPhase('loading');
        else setPhase((p) => (p === 'loading' ? 'done' : p));
    }, [loading]);

    // After a click the context is PATCHED with what the server just agreed to;
    // it is not read again (that was three more requests a click). The next poll
    // reconciles anything the patch could not know; Refresh reads it now (list and
    // counts, one call).
    const refresh = () => { fetchNotifications?.(); };
    const all = notifications || [];
    // Filtered here too: the context's rows may still be the last lane's fetch.
    const rows = lane === 'all' ? all : all.filter((n) => n.lane === lane);
    const q = find.trim().toLowerCase();
    const hit = (n) => !q || `${n.title || ''} ${n.message || ''}`.toLowerCase().includes(q);
    const unreadIn = (id) => (id === 'all' ? unreadCount : (counts?.lanes?.[id] ?? all.filter((n) => n.lane === id && !n.is_read).length));
    const current = openId ? all.find((n) => String(n.id) === String(openId)) : null;

    // Read, and say so if the server refused — the context swallows it.
    const markRead = async (n) => {
        if (n.is_read) return;
        try {
            await api.markNotificationRead(n.id);
            patchRead?.(n.id);
        } catch (e) {
            toast.error(e?.message || 'Could not mark it read.');
        }
    };
    const press = (n) => { open(n.id); markRead(n); };

    // Where it opens, on the map; a row from another space switches first, and a
    // row about a group opens in that group
    const destOf = (n) => destinationOf(n, space);
    const go = (to) => {
        if (to.mustSwitchSpace && to.space === 'personal') switchToPersonal?.();
        navigate(to.url);
    };

    const actionsOf = (n) => {
        const to = destOf(n);
        const answers = actionsFor(n);
        const hasAnswer = answers.some((a) => a.primary);
        const list = [];
        if (to) list.push({ id: 'go', label: 'Go to it', primary: !hasAnswer, run: async () => { await markRead(n); go(to); } });
        answers.forEach((a) => list.push({
            id: a.id,
            label: answerLabel(n, a),
            primary: !!a.primary,
            run: async () => {
                if (a.navigate) { await markRead(n); navigate(a.navigate); return; }
                const result = await api.request(a.request.path, { method: a.request.method, body: a.request.body });
                await markRead(n);
                if (a.followRedirect && result?.redirect_url) navigate(result.redirect_url);
                else toast.success(`${answerLabel(n, a).replace(/…$/, '')}: done.`);
                patchAnswered?.(n.id);
            },
        }));
        // Only what the API lets you dismiss: a feed row addressed to you (canDismiss).
        if (canDismiss(n)) {
            list.push({
                id: 'dismiss', label: 'Dismiss', danger: true,
                confirm: { title: 'Dismiss this?', body: `“${n.title || 'This notification'}” leaves your feed for good.`, label: 'Dismiss' },
                run: async () => { await api.dismissNotification(n.id); close(); patchDismissed?.(n.id); },
            });
        }
        return list;
    };

    const markAll = async () => {
        try {
            await api.markAllNotificationsRead({ surface: 'nucleus', space });
            patchAllRead?.();
        } catch (e) {
            toast.error(e?.message || 'Could not mark everything read.');
        }
    };

    const lanes = useMemo(() => LANES.map((l) => {
        const n = unreadIn(l.id);
        return { id: l.id, label: l.label, count: n > 0 ? (n > 99 ? '99+' : n) : null, hot: 'live' };
    }), [counts, all, unreadCount]); // eslint-disable-line react-hooks/exhaustive-deps

    const shown = (named || countsRead) && (phase === 'done' || rows.length > 0);
    // an unread title in weight with its dot; find dims a row's words, never hides it
    const columns = [
        { id: 'title', label: 'Title', lock: true, min: 200, grow: 2, value: (n) => n.title || 'Notification',
          cell: (n) => <span className={`activity-inbox__name${n.is_read ? '' : ' is-unread'}`}>{n.title || 'Notification'}</span> },
        { id: 'message', label: 'Message', min: 160, grow: 3, value: (n) => n.message || null,
          cell: (n) => n.message || '—' },
        { id: 'when', label: 'When', min: 96, grow: 0, asc: false, className: 'activity-when', value: (n) => when(n.created_at),
          cell: (n) => ago(n.created_at, { never: 'never', dateAfter: 30 }) },
    ];

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={unreadCount || undefined}
            verbs={<>
                <FindField value={find} onChange={setFind} placeholder="Find in the inbox" />
                <Verb icon={<RotateCw />} onClick={refresh}>Refresh</Verb>
                {toggleSound && (
                    <Verb icon={soundEnabled ? <Volume2 /> : <VolumeX />} onClick={toggleSound}
                        title={soundEnabled ? 'A sound plays when something arrives' : 'Arrivals are silent'}>
                        {soundEnabled ? 'Sound on' : 'Sound off'}
                    </Verb>
                )}
                <Verb icon={<CheckCheck />} onClick={markAll} disabled={!unreadCount}>Mark all read</Verb>
            </>}
            inspector={current && (
                <Inspector
                    key={current.id}
                    tag={`${LANE_WORD[current.lane] || 'Inbox'} · ${current.is_read ? 'Read' : 'Unread'}`}
                    title={current.title || 'Notification'}
                    sub={current.message || undefined}
                    actions={actionsOf(current)}
                    onClose={close}
                >
                    <Band title={spaceName}>
                        <Facts items={[
                            { label: 'Arrived', value: ago(current.created_at, { never: 'never', dateAfter: 30 }) },
                            { label: 'On', value: whenOf(current.created_at, { year: true }) || '—' },
                            { label: 'Lane', value: LANE_WORD[current.lane] || '—' },
                            current.type && { label: 'Kind', value: String(current.type).replace(/_/g, ' ') },
                        ]} />
                        {!destOf(current) && <Note>This one points nowhere in Nucleus.</Note>}
                        {current.lane === 'queue' && actionsFor(current).length === 0 && <Note>It clears once what it asks for is done.</Note>}
                    </Band>
                </Inspector>
            )}
        >
            <div className="nx-filters">
                <Segmented label="Lane" options={lanes} value={lane} onChange={pickLane} />
            </div>

            {permission === 'default' && askPermission && (
                <div className="activity-notice activity-notice--inbox">
                    <span>Get told when something needs you, even in another tab.</span>
                    <button type="button" className="nx-link" onClick={askPermission}>Turn on</button>
                </div>
            )}

            {shown && (
                <div className="nx-shelf" style={{ paddingTop: 24, paddingBottom: 48 }}>
                    <TitledSet title={`${LANES.find((l) => l.id === lane)?.label}${inSpace}`} count={rows.length} empty={LANE_EMPTY[lane]}>
                        {rows.length > 0 && (
                            <DataTable
                                id="activity-inbox" label="Inbox" columns={columns} rows={rows} keyOf={(n) => n.id} dimOf={(n) => !hit(n)}
                                sort={{ id: 'when', dir: 'desc' }} currentKey={openId} onOpen={(_, n) => press(n)} actionsOf={actionsOf}
                            />
                        )}
                    </TitledSet>
                </div>
            )}
            {hasMore && rows.length > 0 && (
                <div className="activity-log activity-log--tail">
                    <button type="button" className="activity-more" onClick={loadMore} disabled={loadingMore}>
                        {loadingMore ? 'Reading…' : 'Older'}
                    </button>
                </div>
            )}
        </NucleusPage>
    );
}
